// Detects whether an import data file looks like Revenue, JIB or 1099 data, so the Import screen
// can warn before uploading the wrong type instead of failing later in the backend parser.
//
// Detection is deliberately CONSERVATIVE and FAILS OPEN: anything it cannot fingerprint with
// confidence returns UNKNOWN and the upload proceeds untouched. It is a safeguard, not a gate.
//
// Signatures mirror the backend parsers in TOTDataCommon/Parsers (keep in sync with
// TOTDataCommon/Utility/ImportFileKindDetector.cs):
//
//   REV   EnergyLink XML     XML, root <RevenueData>
//   REV   W_Energy           fixed-width, first 2 chars: 1A 20 33 34 35 37 38
//   REV   QuorumPDS          pipe-delimited, header row starts "BA_NM1|ADDR_LINE1"
//   REV   Enertia2           fixed-width, first char:  B O C P R D T H
//   REV   IntegraAvatar      fixed-width positional, NO record markers (undetectable -> UNKNOWN)
//   REV   SAPCrescent        pipe-delimited, col[0]:   B O C W R D T H
//   REV   Bolo               tab-delimited,  col[0]:   SH H DH D DL CT WT
//   JIB   Enertia2           fixed-width, first char:  C O S I L E A P T
//   JIB   EnergyLinkCSV      quoted CSV,  col[0]:      PRT BA AFE CC INV DET AGE
//   1099  (single parser)    fixed-width, first char:  T A B
//
// Two collisions drive the marker choices below, and are the reason a naive
// "first character" test is not safe:
//   - 1099 uses T, A and B, so a leading A is NOT JIB-exclusive and a leading B is NOT
//     REV-exclusive. Neither is used as a high-confidence marker.
//   - Bolo revenue uses "SH", so a leading S is NOT JIB-exclusive either.
//
// The one-way structural facts that make this work: JIB only ever exists as Enertia fixed-width
// or EnergyLink quoted CSV. So XML, pipe-delimited and tab-delimited files are never JIB.

export const FileKind = {
    REV: 'REV',
    JIB: 'JIB',
    TEN99: 'TEN99',
    UNKNOWN: 'UNKNOWN',
};

//only the leading slice is read; these files can be very large
const SNIFF_BYTES = 65536;
const MAX_SAMPLE_LINES = 50;

const JIB_CSV_CODES = new Set(['PRT', 'BA', 'AFE', 'CC', 'INV', 'DET', 'AGE']);
const WENERGY_CODES = new Set(['1A', '20', '33', '34', '35', '37', '38']);

//record codes that appear in exactly one file kind across every format above
const JIB_ONLY_CHARS = new Set(['I', 'L', 'E']);
const REV_ONLY_CHARS = new Set(['R', 'H', 'W', 'D']);
const TEN99_CHARS = new Set(['T', 'A', 'B']);

const UNKNOWN = { kind: FileKind.UNKNOWN, confidence: null };

const hasAny = (set, predicate) => [...set].some(predicate);

export const detectImportFileKindFromText = (rawText) => {
    if (!rawText) {
        return UNKNOWN;
    }

    //strip BOM
    const text = rawText.replace(/^\uFEFF/, '');

    //XML is only ever revenue - there is no JIB or 1099 xml format
    const head = text.slice(0, 512).trimStart();
    if (head.startsWith('<?xml') || head.includes('<RevenueData')) {
        return { kind: FileKind.REV, confidence: 'high' };
    }

    //drop the final line: the byte slice may have cut it mid-record
    const rawLines = text.split(/\r?\n/);
    const lines = rawLines
        .slice(0, Math.max(0, rawLines.length - 1))
        .filter(line => line.trim() !== '')
        .slice(0, MAX_SAMPLE_LINES);

    if (lines.length === 0) {
        return UNKNOWN;
    }

    const firstLine = lines[0];

    //---- structure checks: none of these formats are ever JIB ----

    //Bolo revenue is tab-delimited
    if (firstLine.split('\t').length >= 3) {
        return { kind: FileKind.REV, confidence: 'high' };
    }

    //QuorumPDS revenue leads with a literal header row
    if (firstLine.toUpperCase().startsWith('BA_NM1|')) {
        return { kind: FileKind.REV, confidence: 'high' };
    }

    //QuorumPDS / SAPCrescent revenue are pipe-delimited (require several fields so a stray
    //pipe inside a fixed-width record doesn't trigger a false revenue verdict)
    if (firstLine.split('|').length >= 5) {
        return { kind: FileKind.REV, confidence: 'high' };
    }

    //EnergyLink JIB is a quoted csv whose first field is a multi-character record code
    if (firstLine.startsWith('"')) {
        const token = firstLine.split(',')[0].replace(/"/g, '').trim().toUpperCase();
        if (JIB_CSV_CODES.has(token)) {
            return { kind: FileKind.JIB, confidence: 'high' };
        }
    }

    //---- plain fixed-width: compare record codes ----

    const twoCharCodes = new Set(lines.map(line => line.slice(0, 2).toUpperCase()));
    const firstChars = new Set(lines.map(line => line.charAt(0).toUpperCase()));

    if (hasAny(twoCharCodes, code => WENERGY_CODES.has(code))) {
        return { kind: FileKind.REV, confidence: 'high' };
    }
    if (hasAny(firstChars, char => JIB_ONLY_CHARS.has(char))) {
        return { kind: FileKind.JIB, confidence: 'high' };
    }
    if (hasAny(firstChars, char => REV_ONLY_CHARS.has(char))) {
        return { kind: FileKind.REV, confidence: 'high' };
    }

    //1099 only has three record codes, so an exclusive subset match is a strong signal.
    //require more than one distinct code so a run of a single shared code (eg. all "T")
    //doesn't read as 1099.
    if (firstChars.size > 1 && [...firstChars].every(char => TEN99_CHARS.has(char))) {
        return { kind: FileKind.TEN99, confidence: 'high' };
    }

    //S or A with nothing above leans Enertia JIB, but overlaps Bolo "SH" and 1099 "A"
    if (hasAny(firstChars, char => char === 'S' || char === 'A')) {
        return { kind: FileKind.JIB, confidence: 'low' };
    }

    //covers IntegraAvatar revenue (no record markers) and any format not yet fingerprinted
    return UNKNOWN;
};

//Reads only the leading slice of the file and classifies it. Never throws.
export const detectImportFileKind = async (file) => {
    try {
        if (!file) {
            return UNKNOWN;
        }
        const text = await file.slice(0, SNIFF_BYTES).text();
        return detectImportFileKindFromText(text);
    }
    catch (ex) {
        //fail open - a detection bug must never prevent a legitimate upload
        console.error('Import file type detection failed:', ex);
        return UNKNOWN;
    }
};

//maps the Import screen's file type dropdown value to the kind we expect the file to be
export const fileKindForFileTypeId = (fileTypeId) => {
    switch (fileTypeId) {
        case '1': return FileKind.REV;
        case '2': return FileKind.JIB;
        case '4': return FileKind.TEN99;
        default: return null;
    }
};

export const fileKindLabel = (kind) => {
    switch (kind) {
        case FileKind.REV: return 'Revenue';
        case FileKind.JIB: return 'JIB';
        case FileKind.TEN99: return '1099';
        default: return 'unrecognized';
    }
};

//true only when we confidently believe the file is a different kind than the one selected
export const isFileKindMismatch = (detected, fileTypeId) => {
    const expected = fileKindForFileTypeId(fileTypeId);
    if (!expected || !detected || detected.kind === FileKind.UNKNOWN) {
        return false;
    }
    return detected.kind !== expected;
};
